import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { useTranslation } from '@gem/modules/i18n';
import { PopupStore } from '@gem/popups';

import Tasks from 'APP/Tasks';
import { ForwardMessagePopup } from 'APP/main/PopupManager/Popups/ForwardMessagePopup/ForwardMessagePopup';
import { PayloadType } from 'APP/model/message/messageModel.types';
import { IMediaGalleryState } from 'APP/types/mediaGallery';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { ChatMessage } from 'STORE/Messages/Message/ChatMessage/ChatMessage';
import { User } from 'STORE/Users/User/User';

import { IMediaGalleryFile, TSetSidebarVisabilityHandler } from '../MediaGallery.types';

interface IMediaGalleryHeaderPresenterParams {
  file: IMediaGalleryFile;
  canDelete: boolean;
  isShowSidebar: boolean;
  onSetSidebarVisibility: TSetSidebarVisabilityHandler;
  galleryState: IMediaGalleryState;
}

interface IMediaGalleryHeaderPresenter {
  childPopup: PopupStore | null;
  file: IMediaGalleryFile | null;
  canDelete: boolean;
  isShowSidebar: boolean;
  onSetSidebarVisibility: TSetSidebarVisabilityHandler;
  galleryState: IMediaGalleryState | null;
  group: Group | null;
  message: ChatMessage | null;
  sender: User | null;
  canBeDelete: boolean;
  canBeForward: boolean;
  payloadId: string | null;
  canBeDownload: boolean;
  onToggleSidebar(): void;
  onDelete(): Promise<void>;
  onForward(): void;
  setFile(file: IMediaGalleryFile): void;
  setCanDelete(canDelete: boolean): void;
  setIsShowSidebar(isShowSidebar: boolean): void;
  setGalleryState(state: IMediaGalleryState): void;
}

export const useMediaGalleryHeaderPresenter = (
  data: IMediaGalleryHeaderPresenterParams
): IMediaGalleryHeaderPresenter => {
  const { file, canDelete, isShowSidebar, onSetSidebarVisibility, galleryState } = data;
  const { t } = useTranslation();

  const presenter = useLocalObservable<IMediaGalleryHeaderPresenter>(() => ({
    childPopup: null,
    file: null,
    canDelete: false,
    isShowSidebar: false,
    onSetSidebarVisibility(): void {
      return;
    },
    galleryState: null,

    get group(): Group | null {
      if (!presenter.galleryState?.groupId) {
        return null;
      }

      return Entities.GroupsStore.getGroupById(presenter.galleryState.groupId);
    },

    get message(): ChatMessage | null {
      return presenter.file?.message || null;
    },

    get sender(): User | null {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore TODO: fix typings
      return presenter.message?.senderUser || null;
    },

    get canBeDelete(): boolean {
      return (
        presenter.canDelete &&
        !!presenter.message?.canBeDelete &&
        ![PayloadType.Album, PayloadType.Article].includes(presenter.message.payload.payloadType)
      );
    },

    get canBeDownload(): boolean {
      return !!presenter.message?.canBeDownloaded;
    },

    get canBeForward(): boolean {
      return !!presenter.message?.canBeForward;
    },

    get payloadId(): string | null {
      return presenter.file?.id || null;
    },

    onToggleSidebar(): void {
      presenter.onSetSidebarVisibility(!presenter.isShowSidebar);
    },

    async onDelete(): Promise<void> {
      const result = await Tasks.app.showConfirm({
        text: t('chat_gallery_confirm_delete'),
        primaryText: t('common_ok'),
        secondaryText: t('common_cancel'),
      });

      if (!result) {
        return;
      }

      const deleteMessage = presenter.message;

      if (!deleteMessage) {
        return;
      }

      const data = { groupId: deleteMessage.groupId, messages: [deleteMessage] };
      if (deleteMessage.isScheduled) {
        Tasks.messaging.deleteScheduledMessages(data);
      } else {
        Tasks.messaging.deleteMessages(data);
      }
    },

    onForward(): void {
      if (!presenter.message) {
        return;
      }
      presenter.childPopup = Tasks.app.openPopup(ForwardMessagePopup, {
        params: { groupId: presenter.message.groupId, messageIds: [presenter.message.id] },
      });
    },

    setFile(file: IMediaGalleryFile): void {
      presenter.file = file;
    },

    setCanDelete(canDelete: boolean): void {
      presenter.canDelete = canDelete;
    },

    setIsShowSidebar(isShowSidebar: boolean): void {
      presenter.isShowSidebar = isShowSidebar;
    },

    setGalleryState(state: IMediaGalleryState): void {
      presenter.galleryState = state;
    },
  }));

  useEffect(() => {
    return (): void => presenter.childPopup?.close();
  }, []);

  useEffect(() => {
    presenter.setFile(file);
    presenter.setCanDelete(canDelete);
    presenter.setIsShowSidebar(isShowSidebar);
    presenter.setGalleryState(galleryState);
    presenter.onSetSidebarVisibility = onSetSidebarVisibility;
  }, [file, canDelete, isShowSidebar, onSetSidebarVisibility, galleryState]);

  return presenter;
};
